import { formatDateTime, formatMoney } from '@comforthub/design-system'
import { formatOrderNr } from './orderNr'

/** Field toggles + branding for printable order work slips. */
export interface WorkSlipFieldToggles {
  showGiftMessage: boolean
  showRecipient: boolean
  showItems: boolean
  showNotes: boolean
  showCustomer: boolean
  showPrices: boolean
  showFulfillment: boolean
  showReadyBy: boolean
}

export interface WorkSlipSettings {
  logoUrl?: string | null
  headerText?: string | null
  fields: WorkSlipFieldToggles
}

export const DEFAULT_WORK_SLIP_FIELDS: WorkSlipFieldToggles = {
  showGiftMessage: true,
  showRecipient: true,
  showItems: true,
  showNotes: true,
  showCustomer: true,
  showPrices: false,
  showFulfillment: true,
  showReadyBy: true,
}

export interface WorkSlipOrderItem {
  id?: string
  /** The offering's name, else the product's: "Courier delivery" for a bouquet sold through it. */
  name?: string | null
  /** The product (inventory) name: what to make. Null on offering-only lines (plans, services). */
  productName?: string | null
  offeringName?: string | null
  quantity?: number | null
  unitPrice?: number | null
  lineTotal?: number | null
  /** The sizes and add-ons chosen for this line; `text` is what the client typed for an "Other". */
  options?: { group?: string | null; name?: string | null; quantity?: number | null; text?: string | null }[] | null
  /** The client's comment on this line (item view). Not the order's courier note. */
  note?: string | null
}

/**
 * What a slip calls a line: the product first, since that is what staff make.
 * The offering ("Courier delivery", "Pickup from store") repeats the Fulfillment
 * block, so it is only the fallback for lines without a product. service-core's
 * SlipLayout uses the same rule, so the cloud slip reads the same.
 */
export function workSlipItemLabel(item: WorkSlipOrderItem): string {
  return item.productName || item.name || item.offeringName || 'Item'
}

/** "Size: Large", "Ribbon × 3", "Event: Other – Name day" (the client's text after an en dash). */
export function workSlipOptionText(option: NonNullable<WorkSlipOrderItem['options']>[number]): string {
  const name = [option.group, option.name].filter(Boolean).join(': ')
  if (!name) return ''
  const withQty = option.quantity && option.quantity > 1 ? `${name} × ${option.quantity}` : name
  const text = option.text?.trim()
  return text ? `${withQty} – ${text}` : withQty
}

/** "Note: Colours she loves", or '' when the line has no comment. Same as the cloud slip. */
export function workSlipNoteText(item: Pick<WorkSlipOrderItem, 'note'>): string {
  const note = item.note?.trim()
  return note ? `Note: ${note}` : ''
}

export interface WorkSlipOrder {
  id: string
  orderNr?: string | null
  status?: string | null
  storeName?: string | null
  customerName?: string | null
  /** Only a deadline when one was stored; otherwise the time the order was placed. */
  readyBy?: string | null
  /** OrderDto.pickupAt: when it must be ready and the courier / client comes. */
  pickupAt?: string | null
  /** False (or missing) while pickupAt is the system's suggestion, true once the store confirmed it. */
  pickupConfirmed?: boolean | null
  notes?: string | null
  fulfillment?: string | null
  giftMessage?: string | null
  recipientName?: string | null
  recipientPhone?: string | null
  items?: WorkSlipOrderItem[] | null
  /**
   * OrderDto.goodsTotal: the client's goods and services after any promo, without the
   * delivery fee. Missing from a service-core older than 2026-10-21.
   */
  goodsTotal?: number | null
  /**
   * OrderDto.promo: the code the order used and the discount it got, which the store pays for.
   * Missing from a service-core older than 2026-10-24.
   */
  promo?: { code?: string | null; discountAmount?: number | null } | null
  targets?: {
    prep?: {
      startAt?: string | null
      endAt?: string | null
    } | null
    pickup?: {
      startAt?: string | null
      endAt?: string | null
    } | null
    delivery?: {
      startAt?: string | null
      endAt?: string | null
      precision?: string | null
    } | null
    completionAt?: string | null
  } | null
}

/**
 * The slip's Total: goods and services only, never the delivery fee (Kim, 2026-10-21 —
 * delivery is not the store's concern). OrderDto.goodsTotal, else the sum of the lines'
 * totals; never order.total, which includes the fee. Null when there is no figure at all.
 * service-core's SlipLayout uses the same rule.
 */
export function workSlipGoodsTotal(order: Pick<WorkSlipOrder, 'goodsTotal' | 'items'>): number | null {
  if (order.goodsTotal != null) return order.goodsTotal
  const totals = (order.items ?? []).map((item) => item.lineTotal).filter((t): t is number => t != null)
  if (totals.length === 0) return null
  return Math.round(totals.reduce((sum, t) => sum + t, 0) * 100) / 100
}

/**
 * The promo lines above the slip's Total (Kim, 2026-10-24: the store pays for its promo codes,
 * so its slip shows them): the goods before the discount, and the code with its discount. Only
 * when the discount is above zero and the server sent goodsTotal, which is already after it;
 * null otherwise, and the slip prints its Total alone. service-core's SlipLayout uses the same rule.
 */
export function workSlipPromoLines(
  order: Pick<WorkSlipOrder, 'goodsTotal' | 'promo'>,
): { goods: number; label: string; discount: number } | null {
  const discount = order.promo?.discountAmount
  if (discount == null || !(discount > 0) || order.goodsTotal == null) return null
  const code = order.promo?.code?.trim()
  return {
    goods: Math.round((order.goodsTotal + discount) * 100) / 100,
    label: code ? `Promo ${code}` : 'Promo',
    discount,
  }
}

/**
 * " (suggested)" while the pickup time is the system's suggestion: on paper it
 * would otherwise read as final. A confirmed time, or one from the prep
 * target, has no mark. Same rule as service-core's SlipLayout.
 */
export function suggestedMark(order: Pick<WorkSlipOrder, 'pickupAt' | 'pickupConfirmed'>): string {
  return order.pickupAt && order.pickupConfirmed !== true ? ' (suggested)' : ''
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function block(title: string, bodyHtml: string): string {
  return `<section class="block"><h2>${escapeHtml(title)}</h2>${bodyHtml}</section>`
}

export function buildOrderWorkSlipHtml(
  order: WorkSlipOrder,
  settings: WorkSlipSettings,
): string {
  const fields = { ...DEFAULT_WORK_SLIP_FIELDS, ...(settings.fields ?? {}) }
  const header = settings.headerText?.trim() || 'Work slip'
  const logoUrl = settings.logoUrl?.trim()

  const parts: string[] = []

  parts.push(`<header class="header">`)
  if (logoUrl) {
    parts.push(`<img class="logo" src="${escapeHtml(logoUrl)}" alt="" />`)
  }
  parts.push(`<h1>${escapeHtml(header)}</h1>`)
  parts.push(`</header>`)

  parts.push(`<div class="meta">`)
  parts.push(
    `<div><strong>Order</strong> ${escapeHtml(order.orderNr || formatOrderNr(order.id))}</div>`,
  )
  if (order.status) {
    parts.push(`<div><strong>Status</strong> ${escapeHtml(order.status)}</div>`)
  }
  if (order.storeName) {
    parts.push(`<div><strong>Store</strong> ${escapeHtml(order.storeName)}</div>`)
  }
  parts.push(`</div>`)

  if (fields.showCustomer && order.customerName) {
    parts.push(block('Client', `<p>${escapeHtml(order.customerName)}</p>`))
  }

  if (fields.showReadyBy) {
    // Always with the date: a printed slip is often read on another day.
    // The pickup time, else the old prep target; never readyBy, which on older
    // orders is the time the order was placed.
    const prepAt = order.pickupAt || order.targets?.prep?.endAt || order.targets?.prep?.startAt
    const ready = prepAt ? formatDateTime(prepAt) + suggestedMark(order) : ''
    const delivery = order.targets?.delivery
    const windowStart = delivery?.startAt ? formatDateTime(delivery.startAt) : ''
    const windowEnd = delivery?.endAt ? formatDateTime(delivery.endAt) : ''
    const lines: string[] = []
    if (ready) lines.push(`<p>Ready by: ${escapeHtml(ready)}</p>`)
    if (windowStart || windowEnd) {
      lines.push(
        `<p>Delivery window: ${escapeHtml(windowStart || '—')} – ${escapeHtml(windowEnd || '—')}</p>`,
      )
    }
    if (lines.length) {
      parts.push(block('Due', lines.join('')))
    }
  }

  if (fields.showFulfillment && order.fulfillment) {
    parts.push(block('Fulfillment', `<p>${escapeHtml(order.fulfillment)}</p>`))
  }

  if (fields.showGiftMessage && order.giftMessage) {
    parts.push(
      block(
        'Gift message',
        `<p class="pre">${escapeHtml(order.giftMessage)}</p>`,
      ),
    )
  }

  if (fields.showRecipient && (order.recipientName || order.recipientPhone)) {
    const lines: string[] = []
    if (order.recipientName) lines.push(`<p>${escapeHtml(order.recipientName)}</p>`)
    if (order.recipientPhone) {
      lines.push(`<p>${escapeHtml(order.recipientPhone)}</p>`)
    }
    parts.push(block('Recipient', lines.join('')))
  }

  if (fields.showNotes && order.notes) {
    parts.push(block('Notes', `<p class="pre">${escapeHtml(order.notes)}</p>`))
  }

  if (fields.showItems && order.items && order.items.length > 0) {
    const rows = order.items
      .map(item => {
        const label = workSlipItemLabel(item)
        const qty =
          item.quantity != null && item.quantity !== 1 ? ` × ${item.quantity}` : ''
        const options = (item.options ?? [])
          .map(workSlipOptionText)
          .filter(Boolean)
          .map(text => `<div class="option">+ ${escapeHtml(text)}</div>`)
          .join('')
        const noteText = workSlipNoteText(item)
        const note = noteText ? `<div class="option note">${escapeHtml(noteText)}</div>` : ''
        const priceCell =
          fields.showPrices && item.lineTotal != null
            ? `<td class="price">${escapeHtml(formatMoney(item.lineTotal))}</td>`
            : fields.showPrices
              ? `<td class="price"></td>`
              : ''
        return `<tr><td>${escapeHtml(label)}${escapeHtml(qty)}${options}${note}</td>${priceCell}</tr>`
      })
      .join('')
    const priceHead = fields.showPrices ? '<th class="price">Total</th>' : ''
    const goodsTotal = fields.showPrices ? workSlipGoodsTotal(order) : null
    const promo = goodsTotal != null ? workSlipPromoLines(order) : null
    const promoRows = promo
      ? `<tr class="sub"><th>Goods</th><td class="price">${escapeHtml(formatMoney(promo.goods))}</td></tr>` +
        `<tr class="sub"><th>${escapeHtml(promo.label)}</th>` +
        `<td class="price">${escapeHtml(formatMoney(-promo.discount))}</td></tr>`
      : ''
    const foot =
      goodsTotal != null
        ? `<tfoot>${promoRows}<tr><th>Total</th><td class="price">${escapeHtml(formatMoney(goodsTotal))}</td></tr></tfoot>`
        : ''
    parts.push(
      block(
        'Items',
        `<table><thead><tr><th>Item</th>${priceHead}</tr></thead><tbody>${rows}</tbody>${foot}</table>`,
      ),
    )
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Work slip ${escapeHtml(order.orderNr || formatOrderNr(order.id))}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: "IBM Plex Sans", "Segoe UI", Helvetica, Arial, sans-serif;
      color: #111;
      margin: 24px;
      font-size: 13px;
      line-height: 1.45;
    }
    .header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
    .logo { max-height: 48px; max-width: 120px; object-fit: contain; }
    h1 { font-size: 20px; margin: 0; font-weight: 700; }
    h2 {
      font-size: 10px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #555;
      margin: 0 0 6px;
    }
    .meta { display: grid; gap: 4px; margin-bottom: 18px; }
    .block { margin-bottom: 14px; page-break-inside: avoid; }
    .pre { white-space: pre-wrap; }
    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; padding: 4px 0; border-bottom: 1px solid #ddd; }
    th.price, td.price { text-align: right; width: 5rem; }
    tfoot th, tfoot td { font-weight: 700; border-bottom: none; border-top: 1px solid #555; }
    tfoot tr.sub th, tfoot tr.sub td { font-weight: 400; }
    tfoot tr.sub + tr.sub th, tfoot tr.sub + tr.sub td { border-top: none; }
    .option { font-size: 11px; color: #555; padding-left: 12px; overflow-wrap: anywhere; }
    .note { white-space: pre-wrap; }
    @media print {
      body { margin: 0; }
    }
  </style>
</head>
<body>
${parts.join('\n')}
</body>
</html>`
}

/** How long to wait for the slip (and its logo) to load before printing anyway. */
const PRINT_LOAD_TIMEOUT_MS = 5_000
/** How long the print frame may stay if the browser never fires afterprint. */
const PRINT_FRAME_TIMEOUT_MS = 60_000

/**
 * Prints the slip from a hidden, same-origin iframe. A pop-up can't do this:
 * window.open() always returns null when `noopener` is set.
 */
export function printOrderWorkSlip(
  order: WorkSlipOrder,
  settings: WorkSlipSettings,
): void {
  const html = buildOrderWorkSlipHtml(order, settings)
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.tabIndex = -1
  // Zero-sized but still rendered: some browsers print a blank page from a
  // frame that is display:none or visibility:hidden.
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'

  let printed = false
  let loadTimer: ReturnType<typeof setTimeout> | undefined
  let removeTimer: ReturnType<typeof setTimeout> | undefined

  const removeFrame = () => {
    clearTimeout(loadTimer)
    clearTimeout(removeTimer)
    iframe.remove()
  }

  const printFrame = () => {
    if (printed) return
    printed = true
    clearTimeout(loadTimer)
    const frameWindow = iframe.contentWindow
    if (!frameWindow) {
      removeFrame()
      return
    }
    try {
      // Remove the frame once the print dialog has closed (on the next tick,
      // so the browser is done with it), or after a while if it never says so.
      frameWindow.addEventListener('afterprint', () => setTimeout(removeFrame, 0))
      removeTimer = setTimeout(removeFrame, PRINT_FRAME_TIMEOUT_MS)
      frameWindow.focus()
      frameWindow.print()
    } catch (err) {
      removeFrame()
      console.error("Couldn't open the print dialog", err)
    }
  }

  try {
    document.body.appendChild(iframe)
    const frameDoc = iframe.contentDocument
    if (!frameDoc) throw new Error('Print frame has no document')
    // Print once the slip, logo included, has loaded. Listen before writing:
    // Chrome and Safari fire load inside close() when there is nothing to fetch.
    iframe.addEventListener('load', () => setTimeout(printFrame, 0), { once: true })
    frameDoc.open()
    frameDoc.write(html)
    frameDoc.close()
    // Don't wait forever for a logo that never loads.
    loadTimer = setTimeout(printFrame, PRINT_LOAD_TIMEOUT_MS)
  } catch {
    removeFrame()
    throw new Error("Couldn't open the print dialog")
  }
}
